/**
 * Delivery item layouts on the delivery list card.
 *
 * From concept1.svg group `items` (layers 1…33 → 1.png…33.png),
 * then shifted into the new-delivery-result-card item slot.
 * Original AABB was relative to plaque (110.5, 746); offset maps that
 * origin onto the new slot (539.5, 758.7) relative to plaque (111, 541).
 *
 * scale — mean of (designWidth / pngWidth, designHeight / pngHeight); informational.
 * Display uses object-fit: contain inside width×height so PNG aspect ratio is preserved.
 */

export type DeliveryItemCardLayout = {
  left: number;
  top: number;
  width: number;
  height: number;
  /** Mean of X/Y design÷png scales (layout AABB may differ from PNG aspect). */
  scale: number;
};

/** concept1.svg items — keyed by asset index (filename without .png). Absolute − plaque (110.5, 746). */
export const DELIVERY_ITEM_CARD_LAYOUTS: Record<number, DeliveryItemCardLayout> =
  {
  1: {
    left: 467.78,
    top: 280.19,
    width: 187.21,
    height: 190.17,
    scale: 0.5731528231,
  },
  2: {
    left: 491.27,
    top: 263.39,
    width: 140.4,
    height: 195.75,
    scale: 0.6220683453,
  },
  3: {
    left: 459.25,
    top: 286.06,
    width: 203.54,
    height: 176.85,
    scale: 0.5637135336,
  },
  4: {
    left: 475.97,
    top: 283.23,
    width: 170.53,
    height: 183.72,
    scale: 0.5604617195,
  },
  5: {
    left: 488.26,
    top: 288.07,
    width: 146.46,
    height: 177.44,
    scale: 0.5602824313,
  },
  6: {
    left: 511.1,
    top: 284.54,
    width: 106.2,
    height: 181.07,
    scale: 0.5085842891,
  },
  7: {
    left: 476.23,
    top: 288.54,
    width: 174.76,
    height: 173.71,
    scale: 0.5621201241,
  },
  8: {
    left: 429.47,
    top: 310.55,
    width: 262.74,
    height: 128.79,
    scale: 0.5597662643,
  },
  9: {
    left: 480.07,
    top: 287.1,
    width: 161.03,
    height: 180.26,
    scale: 0.5602685563,
  },
  10: {
    left: 479.04,
    top: 283.22,
    width: 170.34,
    height: 184.72,
    scale: 0.5818269903,
  },
  11: {
    left: 467.02,
    top: 292.92,
    width: 183.56,
    height: 198,
    scale: 0.7336015326,
  },
  12: {
    left: 488.18,
    top: 285.75,
    width: 146.54,
    height: 178.97,
    scale: 0.5551418781,
  },
  13: {
    left: 485.8,
    top: 293.53,
    width: 151.14,
    height: 162.82,
    scale: 0.5526809249,
  },
  14: {
    left: 517.33,
    top: 285.65,
    width: 88.81,
    height: 178.66,
    scale: 0.5546795839,
  },
  15: {
    left: 490.47,
    top: 289.4,
    width: 143.01,
    height: 169.25,
    scale: 0.5536558589,
  },
  16: {
    left: 490.97,
    top: 281.26,
    width: 148.24,
    height: 183.84,
    scale: 0.5824262328,
  },
  17: {
    left: 492.73,
    top: 290.06,
    width: 137.28,
    height: 170.44,
    scale: 0.552019666,
  },
  18: {
    left: 450.87,
    top: 276.37,
    width: 190,
    height: 191.25,
    scale: 0.4938687983,
  },
  19: {
    left: 464.41,
    top: 287.33,
    width: 193.83,
    height: 175.3,
    scale: 0.5651666667,
  },
  20: {
    left: 458.21,
    top: 325.92,
    width: 206.27,
    height: 97.15,
    scale: 0.5408289839,
  },
  21: {
    left: 481.43,
    top: 301.41,
    width: 154.8,
    height: 147.86,
    scale: 0.5628633703,
  },
  22: {
    left: 469.27,
    top: 282.09,
    width: 182.19,
    height: 188.57,
    scale: 0.5790800113,
  },
  23: {
    left: 451.46,
    top: 284.74,
    width: 219.53,
    height: 181.13,
    scale: 0.561714775,
  },
  24: {
    left: 425.51,
    top: 316.91,
    width: 259.8,
    height: 110.14,
    scale: 0.6334970768,
  },
  25: {
    left: 490.73,
    top: 288.21,
    width: 138.38,
    height: 172.65,
    scale: 0.5728986761,
  },
  26: {
    left: 478.45,
    top: 281.11,
    width: 156.54,
    height: 188.51,
    scale: 0.5853591083,
  },
  27: {
    left: 519.36,
    top: 288.68,
    width: 83.71,
    height: 172.86,
    scale: 0.5404609394,
  },
  28: {
    left: 441.44,
    top: 285.14,
    width: 228.03,
    height: 175.51,
    scale: 0.5845426195,
  },
  29: {
    left: 495.34,
    top: 283.81,
    width: 131.9,
    height: 182.83,
    scale: 0.564225792,
  },
  30: {
    left: 478.48,
    top: 305.66,
    width: 165.77,
    height: 139.46,
    scale: 0.5537193878,
  },
  31: {
    left: 508.52,
    top: 286.83,
    width: 112.82,
    height: 177.06,
    scale: 0.6028745638,
  },
  32: {
    left: 504.94,
    top: 294.63,
    width: 112.22,
    height: 159.08,
    scale: 0.545719443,
  },
  33: {
    left: 473.75,
    top: 274.83,
    width: 190.01,
    height: 188.38,
    scale: 0.5265797839,
  },
  };

/** Old item origin (372.47, 263.53) → PNG item slot (476, 257). */
const CARD_ITEM_SLOT_OFFSET = {
  left: 56.0293,
  top: -6.5313,
} as const;

export function getDeliveryItemCardLayout(
  assetIndex: number,
): DeliveryItemCardLayout | null {
  const layout = DELIVERY_ITEM_CARD_LAYOUTS[assetIndex];
  if (layout == null) {
    return null;
  }
  return {
    ...layout,
    left: layout.left + CARD_ITEM_SLOT_OFFSET.left,
    top: layout.top + CARD_ITEM_SLOT_OFFSET.top,
  };
}
